import type { VehicleSection, VehicleSide } from "@/lib/api/types"

// Segmented car taxonomy for the 3D damage tagger.
//
// We build the car procedurally from named primitives instead of loading a
// GLB, so every selectable panel IS its own mesh, bound to the rigid BE
// taxonomy (VehicleSection + VehicleSide) by construction. No asset-sourcing
// risk, no Draco, no load failures — and the click→part mapping is a lookup,
// never a coordinate inference.
//
// Coordinate frame (three.js, metres-ish):
//   +Z = front of car,  -Z = rear
//   -X = driver side (US left),  +X = passenger side
//   +Y = up,  ground at y=0
//
// Geometry is intentionally low-poly/blocky: this is a tagging diagram, not a
// showroom render. Seams between panel boxes read as panel divisions, which is
// exactly what we want the advisor to tap.

// Surface drives the material look so the car reads as a real vehicle, not a
// monochrome block: body paint (clearcoat), glossy black glass, matte plastic
// trim (bumpers), and lit lamps.
export type PanelSurface = "paint" | "glass" | "trim" | "lamp"

export interface PanelSpec {
  /** Stable unique id — also the React key and the selection key. */
  id: string
  section: VehicleSection
  side: VehicleSide
  /** Advisor-facing label (matches DAMAGE_ZONE_COPY voice). */
  label: string
  /** RoundedBox args [width(x), height(y), depth(z)]. */
  args: [number, number, number]
  /** Mesh position [x, y, z]. */
  pos: [number, number, number]
  /** Optional mesh rotation [x, y, z] in radians (e.g. raked glass). */
  rot?: [number, number, number]
  /** Material family — defaults to "paint". */
  surface?: PanelSurface
}

// 22 taggable panels covering the sedan-relevant VehicleSection values.
// (WHEELS/FRAME are non-surface; LIFT_GATE/TAIL_GATE/PICKUP_BOX are
// body-type-specific — added when SUV/truck silhouettes ship.)
// Tuned so the blocky shapes read as a sedan: long low hood/trunk, a smaller
// set-back greenhouse, body sides between sill (~0.28) and beltline (~0.92).
export const PANELS: PanelSpec[] = [
  // ── Front ──
  { id: "front-bumper", section: "FRONT_BUMPER_GRILLE", side: "FRONT_CENTER", label: "Front bumper", args: [1.66, 0.46, 0.3], pos: [0, 0.5, 2.18], surface: "trim" },
  { id: "hood", section: "HOOD", side: "FRONT_CENTER", label: "Hood", args: [1.46, 0.1, 1.25], pos: [0, 0.92, 1.28], rot: [0.04, 0, 0] },
  { id: "fender-left", section: "FENDER", side: "FRONT_LEFT_SIDE", label: "Front fender (driver)", args: [0.14, 0.5, 1.15], pos: [-0.83, 0.66, 1.32] },
  { id: "fender-right", section: "FENDER", side: "FRONT_RIGHT_SIDE", label: "Front fender (passenger)", args: [0.14, 0.5, 1.15], pos: [0.83, 0.66, 1.32] },
  { id: "headlamp-left", section: "FRONT_LAMPS", side: "FRONT_LEFT_SIDE", label: "Headlamp (driver)", args: [0.32, 0.14, 0.12], pos: [-0.56, 0.84, 2.14], surface: "lamp" },
  { id: "headlamp-right", section: "FRONT_LAMPS", side: "FRONT_RIGHT_SIDE", label: "Headlamp (passenger)", args: [0.32, 0.14, 0.12], pos: [0.56, 0.84, 2.14], surface: "lamp" },

  // ── Greenhouse / top (set back + smaller than the body) ──
  { id: "windshield", section: "WINDSHIELD", side: "FRONT_CENTER", label: "Windshield", args: [1.1, 0.56, 0.05], pos: [0, 1.04, 0.66], rot: [-0.82, 0, 0], surface: "glass" },
  { id: "roof", section: "ROOF", side: "ROOF", label: "Roof", args: [1.12, 0.09, 1.15], pos: [0, 1.27, -0.26] },

  // ── Sides ──
  { id: "front-door-left", section: "FRONT_DOOR", side: "LEFT_SIDE", label: "Front door (driver)", args: [0.1, 0.58, 0.86], pos: [-0.85, 0.64, 0.34] },
  { id: "front-door-right", section: "FRONT_DOOR", side: "RIGHT_SIDE", label: "Front door (passenger)", args: [0.1, 0.58, 0.86], pos: [0.85, 0.64, 0.34] },
  { id: "rear-door-left", section: "REAR_DOOR", side: "LEFT_SIDE", label: "Rear door (driver)", args: [0.1, 0.58, 0.8], pos: [-0.85, 0.64, -0.5] },
  { id: "rear-door-right", section: "REAR_DOOR", side: "RIGHT_SIDE", label: "Rear door (passenger)", args: [0.1, 0.58, 0.8], pos: [0.85, 0.64, -0.5] },
  { id: "rocker-left", section: "ROCKER_PANEL", side: "LEFT_SIDE", label: "Rocker panel (driver)", args: [0.08, 0.16, 1.7], pos: [-0.85, 0.3, -0.05] },
  { id: "rocker-right", section: "ROCKER_PANEL", side: "RIGHT_SIDE", label: "Rocker panel (passenger)", args: [0.08, 0.16, 1.7], pos: [0.85, 0.3, -0.05] },

  // ── Rear ──
  { id: "quarter-left", section: "QUARTER_PANEL", side: "REAR_LEFT_SIDE", label: "Rear quarter (driver)", args: [0.14, 0.52, 1.0], pos: [-0.83, 0.68, -1.4] },
  { id: "quarter-right", section: "QUARTER_PANEL", side: "REAR_RIGHT_SIDE", label: "Rear quarter (passenger)", args: [0.14, 0.52, 1.0], pos: [0.83, 0.68, -1.4] },
  { id: "trunk", section: "TRUNK", side: "REAR_CENTER", label: "Trunk", args: [1.46, 0.1, 0.9], pos: [0, 0.95, -1.62] },
  { id: "rear-bumper", section: "REAR_BUMPER", side: "REAR_CENTER", label: "Rear bumper", args: [1.66, 0.46, 0.3], pos: [0, 0.5, -2.18], surface: "trim" },
  { id: "taillamp-left", section: "REAR_LAMPS", side: "REAR_LEFT_SIDE", label: "Tail lamp (driver)", args: [0.32, 0.14, 0.12], pos: [-0.58, 0.84, -2.14], surface: "lamp" },
  { id: "taillamp-right", section: "REAR_LAMPS", side: "REAR_RIGHT_SIDE", label: "Tail lamp (passenger)", args: [0.32, 0.14, 0.12], pos: [0.58, 0.84, -2.14], surface: "lamp" },
]

export const PANELS_BY_ID: Record<string, PanelSpec> = Object.fromEntries(
  PANELS.map((p) => [p.id, p]),
)

// A tagged panel + its damage detail. severity/damageType borrow the proven
// pattern from the open-source vehicle-damage-selector (severity + type per
// zone) — the structured context the LLM is missing today.
export type DamageSeverity = "minor" | "moderate" | "severe"
export type DamageType =
  | "dent"
  | "scratch"
  | "crack"
  | "broken"
  | "missing"
  | "misaligned"

export interface DamageTag {
  panelId: string
  section: VehicleSection
  side: VehicleSide
  label: string
  severity: DamageSeverity
  damageType: DamageType
}

export const DAMAGE_TYPE_LABEL: Record<DamageType, string> = {
  dent: "Dent",
  scratch: "Scratch",
  crack: "Crack",
  broken: "Broken",
  missing: "Missing",
  misaligned: "Misaligned",
}

export const SEVERITY_LABEL: Record<DamageSeverity, string> = {
  minor: "Minor",
  moderate: "Moderate",
  severe: "Severe",
}

export function newTagFromPanel(p: PanelSpec): DamageTag {
  return {
    panelId: p.id,
    section: p.section,
    side: p.side,
    label: p.label,
    severity: "moderate",
    damageType: "dent",
  }
}
